/**
 * メール返信作成 hook
 * メール詳細画面からの返信メールを AI 生成し、Gmail API 経由で送信する.
 */
import { fetchAuthSession } from "@aws-amplify/auth";
import type { EmailConnection } from "hooks/use-email-integrations-api";
import type { EmailDocument } from "hooks/useEmailsRestApi";
import { restClient } from "lib/rest-client";
import { useCallback, useRef, useState } from "react";

type EmailConnectionListResponse = { items: EmailConnection[] };

// REST API ベース URL
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "";
const REST_API_BASE_URL = `${API_ENDPOINT}/api`;

// =====================================
// Types
// =====================================

export type RecipientType = "to" | "cc" | "bcc";

export type Recipient = {
  email: string;
  name: string;
  type: RecipientType;
};

export type ReplyDraft = {
  sourceEmailId: string;
  /**
   * reply_emails.id (UUID). 返信管理画面など、OpenSearch 外のメールを源として
   * AI 生成する場合に設定する。 sourceEmailId を空のままバックエンドへ送る代わりに
   * このフィールドが優先されてスレッド全体がコンテキスト化される.
   */
  replyEmailId?: string | null;
  fromEmail: string;
  fromName: string;
  recipients: Recipient[];
  subject: string;
  body: string;
  aiGenerated: boolean;
  aiPromptType: string | null;
  /** 自社マージン（万円）. AI生成時の単価補正に使用 */
  margin: number;
  /** 提案対象情報. reply_matter/reply_hr の AI 生成時に使用 */
  proposalInfo: string;
  /** Sales Board / 注力案件 経由の送信時のみ設定 */
  salesPipelineId?: string | null;
  /** 注力案件 経由の送信時のみ設定 */
  focusMatterId?: string | null;
  /** 返信先メールの RFC2822 Message-ID. Gmail 経路の In-Reply-To に使う */
  inReplyToMessageId?: string | null;
  /** References ヘッダに入れる Message-ID チェーン */
  references?: string[];
  /** Gmail スレッド ID. 指定するとそのスレッドに参加する */
  providerThreadId?: string | null;
};

/** ReplyEmail / SentEmail など OpenSearch 以外のソースから返信を起こすためのシード. */
export type ReplyComposeSeed = {
  /** 宛先メールアドレス */
  toEmail: string;
  /** 宛先表示名 */
  toName?: string | null;
  /** 件名（"Re: ..." プレフィックスはここで付与済みを想定） */
  subject: string;
  /** OpenSearch ID がある場合のみ（AI 生成のソースにのみ使用） */
  sourceEmailId?: string | null;
  /** reply_emails.id (UUID). 返信管理画面からの seed 専用. AI 生成のソースに使う. */
  replyEmailId?: string | null;
  /** 返信先メッセージの RFC2822 Message-ID. Gmail 経路の In-Reply-To に使う */
  inReplyToMessageId?: string | null;
  /** References ヘッダに入れる Message-ID チェーン */
  references?: string[];
  /** Gmail スレッド ID. 指定するとそのスレッドに参加する */
  providerThreadId?: string | null;
  /** Sales Board カードや注力案件経由で起動した場合 */
  salesPipelineId?: string | null;
  focusMatterId?: string | null;
};

/** マージンのデフォルト値（万円）. matching_scorer の既定値と整合 */
const DEFAULT_MARGIN = 5;

export type PromptType =
  | "reply_matter"
  | "reply_hr"
  | "inquiry"
  | "negotiate"
  | "schedule"
  | "decline"
  | "followup"
  | "custom";

type EmailSendResponse = {
  success: boolean;
  message: string;
  sentEmailId: string | null;
};

export type UseEmailComposeResult = {
  draft: ReplyDraft | null;
  isGenerating: boolean;
  isSending: boolean;
  error: string | null;
  startReply: (email: EmailDocument) => void;
  /**
   * OpenSearch Email を経由しない返信開始（Sales Board / 返信管理画面からの呼び出し用）
   */
  startReplyFromSeed: (seed: ReplyComposeSeed) => void;
  setField: <K extends keyof ReplyDraft>(
    field: K,
    value: ReplyDraft[K],
  ) => void;
  addRecipient: (recipient: Recipient) => void;
  removeRecipient: (email: string, type: RecipientType) => void;
  generateBody: (promptType: PromptType, freeText?: string) => void;
  sendEmail: () => Promise<boolean>;
  resetDraft: () => void;
  cancelGeneration: () => void;
};

/**
 * メール返信作成を管理する hook
 * @return {UseEmailComposeResult} 返信メール作成の状態と操作関数
 */
export function useEmailCompose(): UseEmailComposeResult {
  const [draft, setDraft] = useState<ReplyDraft | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /**
   * 送信者一覧を取得して draft.fromEmail / fromName に反映する
   * @return {void}
   */
  const hydrateDefaultSender = useCallback(() => {
    (async () => {
      try {
        const response = await restClient.get<EmailConnectionListResponse>(
          "/email-integrations",
        );
        const active = (response.items ?? []).find(
          c => c.provider === "gmail" && c.status === "connected",
        );

        if (active) {
          setDraft(prev =>
            prev
              ? {
                  ...prev,
                  fromEmail: active.emailAddress,
                  fromName: active.displayName ?? active.emailAddress,
                }
              : prev,
          );
        }
      } catch {
        // 送信者取得失敗は non-fatal: ドラフトは維持して手動選択させる
      }
    })();
  }, []);

  const startReply = useCallback(
    (email: EmailDocument) => {
      abortRef.current?.abort();
      setDraft({
        sourceEmailId: email.id,
        fromEmail: "",
        fromName: "",
        recipients: [
          {
            email: email.from.address,
            name: email.from.name,
            type: "to",
          },
        ],
        subject: `Re: ${(email.subject || "").replace(/^Re:\s*/i, "")}`,
        body: "",
        aiGenerated: false,
        aiPromptType: null,
        margin: DEFAULT_MARGIN,
        proposalInfo: "",
        // OpenSearch 由来の起動では messageId / threadId を持たない.
        // backend が source_email_id から自前で引けない構成のため、ここでも null。
        // → Gmail 経路ではスレッド継続できないが、新規スレッドとして送信される。
        inReplyToMessageId: null,
        references: [],
        providerThreadId: null,
      });
      setError(null);
      setIsGenerating(false);
      hydrateDefaultSender();
    },
    [hydrateDefaultSender],
  );

  const startReplyFromSeed = useCallback(
    (seed: ReplyComposeSeed) => {
      abortRef.current?.abort();
      setDraft({
        sourceEmailId: seed.sourceEmailId ?? "",
        replyEmailId: seed.replyEmailId ?? null,
        fromEmail: "",
        fromName: "",
        recipients: [
          {
            email: seed.toEmail,
            name: seed.toName ?? "",
            type: "to",
          },
        ],
        subject: seed.subject.startsWith("Re:")
          ? seed.subject
          : `Re: ${seed.subject.replace(/^Re:\s*/i, "")}`,
        body: "",
        aiGenerated: false,
        aiPromptType: null,
        margin: DEFAULT_MARGIN,
        proposalInfo: "",
        salesPipelineId: seed.salesPipelineId ?? null,
        focusMatterId: seed.focusMatterId ?? null,
        inReplyToMessageId: seed.inReplyToMessageId ?? null,
        references: seed.references ?? [],
        providerThreadId: seed.providerThreadId ?? null,
      });
      setError(null);
      setIsGenerating(false);
      hydrateDefaultSender();
    },
    [hydrateDefaultSender],
  );

  const setField = useCallback(
    <K extends keyof ReplyDraft>(field: K, value: ReplyDraft[K]) => {
      setDraft(prev => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const addRecipient = useCallback((recipient: Recipient) => {
    setDraft(prev => {
      if (!prev) return prev;
      // 同じメール+種別の重複を防止
      const exists = prev.recipients.some(
        r => r.email === recipient.email && r.type === recipient.type,
      );
      if (exists) return prev;
      return { ...prev, recipients: [...prev.recipients, recipient] };
    });
  }, []);

  const removeRecipient = useCallback((email: string, type: RecipientType) => {
    setDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        recipients: prev.recipients.filter(
          r => !(r.email === email && r.type === type),
        ),
      };
    });
  }, []);

  const generateBody = useCallback(
    (promptType: PromptType, freeText?: string) => {
      if (!draft) return;

      // キャプチャ（stale closure 回避）
      const { sourceEmailId, replyEmailId, fromName, margin, proposalInfo } =
        draft;

      // 既存のストリーミングをキャンセル
      if (abortRef.current) {
        abortRef.current.abort();
      }

      setError(null);
      setIsGenerating(true);
      setDraft(prev =>
        prev
          ? { ...prev, body: "", aiGenerated: true, aiPromptType: promptType }
          : prev,
      );

      const controller = new AbortController();
      abortRef.current = controller;

      (async () => {
        try {
          const session = await fetchAuthSession();
          const token = session.tokens?.idToken?.toString();
          if (!token) {
            setError("認証トークンの取得に失敗しました");
            setIsGenerating(false);
            return;
          }

          const response = await fetch(
            `${REST_API_BASE_URL}/compose/generate`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                // biome-ignore lint/style/useNamingConvention: HTTP header
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                sourceEmailId,
                replyEmailId: replyEmailId ?? null,
                promptType,
                freeText: freeText || null,
                fromName: fromName || null,
                margin,
                proposalInfo: proposalInfo || null,
              }),
              signal: controller.signal,
            },
          );

          if (!response.ok) {
            setError(`AI生成エラー: HTTP ${response.status}`);
            setIsGenerating(false);
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            setError("レスポンスの読み取りに失敗しました");
            setIsGenerating(false);
            return;
          }

          const decoder = new TextDecoder();
          let buffer = "";

          let done = false;
          while (!done) {
            const readResult = await reader.read();
            done = readResult.done;
            if (done) break;

            buffer += decoder.decode(readResult.value, { stream: true });

            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const dataStr = line.slice(6);

              try {
                const data = JSON.parse(dataStr);

                if (data.type === "chunk") {
                  setDraft(prev =>
                    prev ? { ...prev, body: prev.body + data.text } : prev,
                  );
                } else if (data.type === "done") {
                  setIsGenerating(false);
                } else if (data.type === "error") {
                  setError(data.message || "AI生成に失敗しました");
                  setIsGenerating(false);
                }
              } catch {
                // JSON パース失敗は無視
              }
            }
          }

          setIsGenerating(false);
        } catch (e) {
          if ((e as Error).name !== "AbortError") {
            setError(e instanceof Error ? e.message : "AI生成に失敗しました");
            setIsGenerating(false);
          }
        } finally {
          abortRef.current = null;
        }
      })();
    },
    [draft],
  );

  const sendEmail = useCallback(async (): Promise<boolean> => {
    if (!draft) return false;

    const toRecipients = draft.recipients.filter(r => r.type === "to");
    if (toRecipients.length === 0) {
      setError("宛先（To）を1件以上指定してください");
      return false;
    }

    setIsSending(true);
    setError(null);

    try {
      const response = await restClient.post<EmailSendResponse>(
        "/compose/send",
        {
          sourceEmailId: draft.sourceEmailId || null,
          fromEmail: draft.fromEmail,
          fromName: draft.fromName,
          recipients: draft.recipients.map(r => ({
            email: r.email,
            name: r.name || null,
            type: r.type,
          })),
          subject: draft.subject,
          body: draft.body,
          sendType: "reply",
          aiGenerated: draft.aiGenerated,
          aiPromptType: draft.aiPromptType,
          salesPipelineId: draft.salesPipelineId ?? null,
          focusMatterId: draft.focusMatterId ?? null,
          inReplyToMessageId: draft.inReplyToMessageId ?? null,
          references: draft.references ?? [],
          providerThreadId: draft.providerThreadId ?? null,
        },
      );

      if (!response.success) {
        setError(response.message || "送信に失敗しました");
        return false;
      }

      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "送信に失敗しました");
      return false;
    } finally {
      setIsSending(false);
    }
  }, [draft]);

  const resetDraft = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setDraft(null);
    setError(null);
    setIsGenerating(false);
    setIsSending(false);
  }, []);

  const cancelGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  return {
    draft,
    isGenerating,
    isSending,
    error,
    startReply,
    startReplyFromSeed,
    setField,
    addRecipient,
    removeRecipient,
    generateBody,
    sendEmail,
    resetDraft,
    cancelGeneration,
  };
}
